import React, { useContext, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import PageContext from "../../components/Common/PageContext";
import PrinterSelect from "../../components/Printer/PrinterSelect";
import {
	Box,
	Button,
	Card,
	CardContent,
	FormControl,
	Grid2 as Grid,
	IconButton,
	InputLabel,
	Menu,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from "@mui/material";
import { ProductLabelRequest } from "../../common/api/Models/ProductLabelRequest";
import { ErrorResponse } from "../../common/api/Models/ErrorResponse";
import { LabelConfiguration } from "../../common/api/Models/LabelConfiguration";
import { Add, DeleteOutline, MoreVert, Remove } from "@mui/icons-material";
import { LargeActionButton } from "../../components/Common/LargeActionButton";
import {
	getLabelConfigurations,
	getLabelProductsByType,
	postLabelRequests,
	searchProduct,
} from "../../services/apiHelper";
import { appInsights } from "../../services/telemetryService";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import { TitleValueDisplay } from "../../components/TitleValueDisplay";
import { TitleValueType } from "../../components/TitleValueDisplay/TitleValueDisplay";
import { ModalBase } from "../../components/Modal/ModalBase";
import alertTypeEnum from "../../util/alertTypeEnum";
import { translateError } from "../../util/axiosUtils";
import ProductLabelQuantityField from "./ProductLabelQuantityField";
import { SearchBarContainer } from "../../components/Search/components/SearchBar/SearchBarContainer";
import { searchHelperText } from "../../components/Search/constants";
import LabelSheetStartPicker from "../../components/Labels/LabelSheetStartPicker";
import { PrinterOutputType } from "../../util/printerEnums";
import { isProductLabelConfiguration } from "../../util/labelConfigurationUtil";

const PRODUCT_LABELS_STORAGE_KEY_PREFIX = "product-label-requests";

const getProductLabelsStorageKey = (location: string | number | undefined) =>
	`${PRODUCT_LABELS_STORAGE_KEY_PREFIX}-${location ?? "default"}`;

const isStoredProductLabelRequest = (value: any): value is ProductLabelRequest => {
	return (
		value &&
		typeof value.sku === "string" &&
		typeof value.quantity === "number" &&
		typeof value.description === "string" &&
		typeof value.model === "string" &&
		typeof value.barcode === "string"
	);
};

export default function ProductLabels() {
	const [pageContext, updatePageContextAsync] = useContext(PageContext);
	const { selectedLabelPrinterId, printers, userLocation } = pageContext;
	const [selectedPrinter, setSelectedPrinter] = useState(selectedLabelPrinterId);
	const [labelRequests, setLabelRequests] = useState<ProductLabelRequest[]>([]);
	const [availableTemplates, setAvailableTemplates] = useState<LabelConfiguration[]>([]);
	const [selectedTemplate, setSelectedTemplate] = useState<LabelConfiguration | undefined>(undefined);
	const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");
	const [transferNumber, setTransferNumber] = useState("");
	const [isPOLoading, setIsPOLoading] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isLabelErrored, setIsLabelErrored] = useState(false);
	const [startColumn, setStartColumn] = useState(0);
	const [startRow, setStartRow] = useState(0);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [showPOModal, setShowPOModal] = useState(false);
	const [showTransferModal, setShowTransferModal] = useState(false);
	const [showClearConfirmation, setShowClearConfirmation] = useState(false);
	const [hasLoadedStoredLabels, setHasLoadedStoredLabels] = useState(false);
	const [alertModal, setAlertModal] = useState({
		isOpen: false,
		title: "",
		message: "",
		alertType: "",
		primaryButtonName: "",
		secondaryButtonName: "",
	});
	const productLabelTemplates = useMemo(
		() => availableTemplates.filter(isProductLabelConfiguration),
		[availableTemplates]
	);

	useEffect(() => {
		try {
			const rawStoredValue = localStorage.getItem(getProductLabelsStorageKey(userLocation));
			if (!rawStoredValue) {
				return;
			}

			const parsedValue = JSON.parse(rawStoredValue);
			if (!Array.isArray(parsedValue)) {
				localStorage.removeItem(getProductLabelsStorageKey(userLocation));
				return;
			}

			const validStoredRequests = parsedValue.filter(
				(request): request is ProductLabelRequest =>
					isStoredProductLabelRequest(request) && Number.isFinite(request.quantity) && request.quantity > 0
			);

			setLabelRequests(validStoredRequests);
		} catch (error) {
			console.error("Failed to restore stored label requests", error);
			localStorage.removeItem(getProductLabelsStorageKey(userLocation));
		} finally {
			setHasLoadedStoredLabels(true);
		}
	}, [userLocation]);

	useEffect(() => {
		if (!hasLoadedStoredLabels) {
			return;
		}

		const storageKey = getProductLabelsStorageKey(userLocation);
		if (labelRequests.length === 0) {
			localStorage.removeItem(storageKey);
			return;
		}

		localStorage.setItem(storageKey, JSON.stringify(labelRequests));
	}, [hasLoadedStoredLabels, labelRequests, userLocation]);

	// Load all templates for this location on mount
	useEffect(() => {
		getLabelConfigurations(userLocation)
			.then((res) => {
				setAvailableTemplates(res.data as LabelConfiguration[]);
			})
			.catch((err) => console.error("Failed to load label configurations", err));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userLocation]);

	// When the selected printer changes, reset start position and auto-select template if only one matches
	useEffect(() => {
		setStartColumn(0);
		setStartRow(0);

		const printerData = printers?.find((p) => p.id === selectedPrinter);
		const outputType = printerData?.outputType;

		const matchingTemplates = outputType
			? productLabelTemplates.filter((t) => t.printerOutputType === outputType)
			: productLabelTemplates;

		if (matchingTemplates.length === 1) {
			setSelectedTemplate(matchingTemplates[0]);
		} else {
			setSelectedTemplate(undefined);
		}
	}, [selectedPrinter, printers, productLabelTemplates]);

	const onScan = (e: any) => {
		getAndSetProduct(e.data.BarcodeValue);
	};

	// Handle barcode scanning
	useEffect(() => {
		document.addEventListener("onScan", onScan);
		return () => {
			document.removeEventListener("onScan", onScan);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onPrinterSelect = async (e) => {
		setSelectedPrinter(e.target.value);
		await updatePageContextAsync({ selectedLabelPrinterId: e.target.value });
	};

	const onTemplateSelect = (e: SelectChangeEvent<string>) => {
		const format = e.target.value;
		const template = productLabelTemplates.find((t) => t.format === format);
		setSelectedTemplate(template);
		setStartColumn(0);
		setStartRow(0);
	};

	const isNotFoundError = (error: unknown) => {
		return (error as AxiosError)?.response?.status === 404;
	};

	const getAndSetProduct = async (searchTerm: string) => {
		try {
			setIsLoading(true);
			setIsLabelErrored(false);
			const searchData = await searchProduct(searchTerm);
			const product = searchData.data;

			setLabelRequests((prev) => {
				const existing = prev.find((m) => m.sku === product.sku);
				if (existing) {
					return prev.map((lr) => (lr.sku === product.sku ? { ...lr, quantity: lr.quantity + 1 } : lr));
				}
				return [
					{
						sku: product.sku,
						description: product.description,
						model: product.model,
						barcode: product.barcode,
						onlineDescription: product.onlineDescription,
						quantity: 1,
					},
					...prev,
				];
			});
		} catch (error) {
			console.error("Error occurred", error);
			showErrorModal("Could not find product", `Failed to find product.`);
		} finally {
			setIsLoading(false);
		}
	};

	const handleLoadFromPO = async () => {
		if (!purchaseOrderNumber.trim()) return;
		setIsPOLoading(true);
		try {
			const res = await getLabelProductsByType(purchaseOrderNumber.trim(), "PurchaseOrder");
			const poProducts: Array<{
				sku: string;
				quantity: number;
				description: string;
				model: string;
				barcode: string;
			}> = res.data;
			setLabelRequests((prev) => {
				const merged = [...prev];
				for (const po of poProducts) {
					if (!merged.find((m) => m.sku === po.sku)) {
						merged.unshift({
							sku: po.sku,
							description: po.description,
							model: po.model,
							barcode: po.barcode,
							onlineDescription: po.description,
							quantity: po.quantity,
						});
					}
				}
				return merged;
			});
			setPurchaseOrderNumber("");
			setShowPOModal(false);
			setAnchorEl(null);
		} catch (error) {
			if (isNotFoundError(error)) {
				showErrorModal("Could not load PO or Invoice", `Could not find PO or Invoice for number "${purchaseOrderNumber.trim()}"`);
			} else {
				showErrorModal("Could not load PO or Invoice", `Failed to load PO or Invoice. ${translateError(error as AxiosError<ErrorResponse>)}`);
			}
		} finally {
			setIsPOLoading(false);
		}
	};

	const handleLoadFromTransfer = async () => {
		if (!transferNumber.trim()) return;
		setIsPOLoading(true);
		try {
			const res = await getLabelProductsByType(transferNumber.trim(), "Transfer");
			const transferProducts: Array<{
				sku: string;
				quantity: number;
				description: string;
				model: string;
				barcode: string;
			}> = res.data;
			setLabelRequests((prev) => {
				const merged = [...prev];
				for (const transfer of transferProducts) {
					if (!merged.find((m) => m.sku === transfer.sku)) {
						merged.unshift({
							sku: transfer.sku,
							description: transfer.description,
							model: transfer.model,
							barcode: transfer.barcode,
							onlineDescription: transfer.description,
							quantity: transfer.quantity,
						});
					}
				}
				return merged;
			});
			setTransferNumber("");
			setShowTransferModal(false);
			setAnchorEl(null);
		} catch (error) {
			if (isNotFoundError(error)) {
				showErrorModal("Could not load transfer", `Could not find transfer ${transferNumber.trim()}`);
			} else {
				showErrorModal("Could not load transfer", `Failed to load transfer. ${translateError(error as AxiosError<ErrorResponse>)}`);
			}
		} finally {
			setIsPOLoading(false);
		}
	};

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleClearAllClick = () => {
		setShowClearConfirmation(true);
		setAnchorEl(null);
	};

	const handleConfirmClear = () => {
		setLabelRequests([]);
		setShowClearConfirmation(false);
	};

	const handleQuantityChange = (sku: string, quantity: number) => {
		setLabelRequests((prev) => prev.map((lr) => (lr.sku === sku ? { ...lr, quantity: lr.quantity + quantity } : lr)));
	};

	const setQuantityChange = (sku: string, quantity: number | string) => {
		const newQuantity = quantity === "" ? 0 : parseInt(quantity as string, 10);
		setLabelRequests((prev) => prev.map((lr) => (lr.sku === sku ? { ...lr, quantity: newQuantity } : lr)));
	};

	const onDelete = (sku: string) => {
		setLabelRequests((prev) => prev.filter((m) => m.sku !== sku));
	};

	const handlePrint = async () => {
		if (!selectedPrinter || !selectedTemplate) {
			return;
		}

		setIsLoading(true);
		const printerData = printers?.find((p) => p.id === selectedPrinter);
		await postLabelRequests(
			selectedPrinter,
			labelRequests,
			userLocation,
			printerData?.outputType,
			startColumn,
			startRow,
			selectedTemplate.format
		)
			.then(() => {
				appInsights.trackEvent(
					{ name: "ProductLabelsPrinted" },
					{
						locationId: userLocation,
						format: selectedTemplate.format,
						labelCount: labelRequests.reduce((sum, lr) => sum + (lr.quantity ?? 0), 0),
						printerId: selectedPrinter,
						printerName: printerData?.name,
					}
				);
				showSuccessModal("Success", "Labels printed successfully");
			})
			.catch(async (error) => {
				await updatePageContextAsync({
					snackBarAlert: {
						isOpen: true,
						message: translateError(error),
						severity: "error",
					},
				});
			})
			.finally(() => setIsLoading(false));
	};

	const clearList = () => {
		if (!isLabelErrored) {
			setLabelRequests([]);
		}
		setIsLabelErrored(false);
		closeModal();
	};

	const printerData = printers?.find((p) => p.id === selectedPrinter);
	const hasA4Templates = productLabelTemplates.some((template) => template.printerOutputType === PrinterOutputType.A4);
	const hasZebraTemplates = productLabelTemplates.some(
		(template) => template.printerOutputType === PrinterOutputType.Zebra
	);
	const availablePrinters =
		printers?.filter((printer) => {
			if (printer.outputType === PrinterOutputType.A4) {
				return hasA4Templates;
			}

			if (printer.outputType === PrinterOutputType.Zebra) {
				return hasZebraTemplates;
			}

			return true;
		}) ?? [];
	// Only include templates whose printerOutputType exactly matches the selected printer.
	const filteredTemplates = printerData?.outputType
		? productLabelTemplates.filter((t) => t.printerOutputType === printerData.outputType)
		: productLabelTemplates;

	const hasSelectedTemplate =
		!!selectedTemplate && filteredTemplates.some((template) => template.format === selectedTemplate.format);
	const templateRequiresSelection = filteredTemplates.length > 1 && !hasSelectedTemplate;
	const templateSelectionMissing = filteredTemplates.length > 0 && !hasSelectedTemplate;
	const isPrintButtonDisabled =
		labelRequests.length === 0 || !selectedPrinter || filteredTemplates.length === 0 || templateSelectionMissing;

	useEffect(() => {
		if (!selectedPrinter) {
			return;
		}

		const selectedPrinterIsAvailable = availablePrinters.some((printer) => printer.id === selectedPrinter);
		if (selectedPrinterIsAvailable) {
			return;
		}

		setSelectedPrinter(undefined);
		updatePageContextAsync({ selectedLabelPrinterId: undefined });
	}, [availablePrinters, selectedPrinter, updatePageContextAsync]);

	const showErrorModal = (title: string, message: string) => {
		setIsLabelErrored(true);
		setAlertModal({
			isOpen: true,
			title,
			message,
			alertType: alertTypeEnum.ERROR,
			primaryButtonName: "OK",
			secondaryButtonName: "",
		});
	};

	const showSuccessModal = (title: string, message: string) => {
		setIsLabelErrored(false);
		setAlertModal({
			isOpen: true,
			title,
			message,
			alertType: alertTypeEnum.SUCCESS,
			primaryButtonName: "Clear Products",
			secondaryButtonName: "Keep Products",
		});
	};

	const closeModal = () => {
		setAlertModal({
			isOpen: false,
			title: "",
			message: "",
			alertType: alertTypeEnum.INFO,
			primaryButtonName: "",
			secondaryButtonName: "",
		});
	};

	return (
		<>
			<LoadingDialog isOpen={isLoading || isPOLoading} message="Loading" />
			<ModalBase
				{...alertModal}
				onPrimaryButtonClick={clearList}
				onSecondaryButtonClick={closeModal}
				onClose={closeModal}
			/>
			<Grid container direction="row" alignItems="center">
				{/* Search bar and actions button in the same row */}
				<Grid size={12} sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 1 }}>
					<Box sx={{ flex: 1 }}>
						<SearchBarContainer
							handleSearch={getAndSetProduct}
							helperText={searchHelperText.productLabel}
							clearAfterSearch={true}
						/>
					</Box>
					<IconButton
						size="large"
						onClick={handleMenuOpen}
						aria-label="load actions menu"
						sx={{
							border: "1.5px solid",
							borderColor: "grey.400",
							borderRadius: 0,
							ml: 1,
							width: 56,
							height: 56,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mt: 1,
						}}
					>
						<MoreVert fontSize="large" />
					</IconButton>
					<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
						<MenuItem onClick={() => setShowPOModal(true)}>Load from PO or Invoice</MenuItem>
						<MenuItem onClick={() => setShowTransferModal(true)}>Load from Transfer</MenuItem>
						<MenuItem onClick={handleClearAllClick} disabled={labelRequests.length === 0}>
							Clear All Items
						</MenuItem>
					</Menu>
				</Grid>

				{/* Load from PO Modal */}
				<ModalBase
					isOpen={showPOModal}
					title="Load from PO or Invoice"
					message="Enter the purchase order or invoice number to load products."
					children={
						<Box sx={{ pt: 2 }}>
							<TextField
								autoFocus
								label="PO / Invoice Number"
								size="small"
								value={purchaseOrderNumber}
								onChange={(e) => setPurchaseOrderNumber(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleLoadFromPO();
								}}
								fullWidth
							/>
						</Box>
					}
					alertType={alertTypeEnum.INFO}
					primaryButtonName="Load"
					secondaryButtonName="Cancel"
					onPrimaryButtonClick={handleLoadFromPO}
					onSecondaryButtonClick={() => {
						setShowPOModal(false);
						setPurchaseOrderNumber("");
					}}
					onClose={() => {
						setShowPOModal(false);
						setPurchaseOrderNumber("");
					}}
				/>

				{/* Load from Transfer Modal */}
				<ModalBase
					isOpen={showTransferModal}
					title="Load from Transfer"
					message="Enter the transfer number to load products."
					children={
						<Box sx={{ pt: 2 }}>
							<TextField
								autoFocus
								label="Transfer Number"
								size="small"
								value={transferNumber}
								onChange={(e) => setTransferNumber(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleLoadFromTransfer();
								}}
								fullWidth
							/>
						</Box>
					}
					alertType={alertTypeEnum.INFO}
					primaryButtonName="Load"
					secondaryButtonName="Cancel"
					onPrimaryButtonClick={handleLoadFromTransfer}
					onSecondaryButtonClick={() => {
						setShowTransferModal(false);
						setTransferNumber("");
					}}
					onClose={() => {
						setShowTransferModal(false);
						setTransferNumber("");
					}}
				/>

				{/* Clear Confirmation Modal */}
				<ModalBase
					isOpen={showClearConfirmation}
					title="Clear All Items"
					message="Are you sure you want to remove all items from the list? This action cannot be undone."
					alertType={alertTypeEnum.WARNING}
					primaryButtonName="Clear"
					secondaryButtonName="Cancel"
					onPrimaryButtonClick={handleConfirmClear}
					onSecondaryButtonClick={() => setShowClearConfirmation(false)}
					onClose={() => setShowClearConfirmation(false)}
				/>

				{/* Product list */}
				<Grid size={12}>
					<Box sx={{ width: "100%", py: 1 }}>
						{labelRequests.map((labelRequest) => (
							<Card key={labelRequest.sku} sx={{ mb: 1 }}>
								<CardContent>
									<Grid container alignItems="center" spacing={2}>
										{/* Product details */}
										<Grid size={12}>
											<Typography variant="body1">
												<strong>{labelRequest.onlineDescription}</strong>
											</Typography>
											<Grid container direction="row">
												<Grid size={{ xs: 6, sm: 2 }}>
													<TitleValueDisplay title="SKU " value={labelRequest.sku} type={TitleValueType.Vertical} />
												</Grid>
												<Grid size={{ xs: 6, sm: 2 }}>
													<TitleValueDisplay
														title="Barcode "
														value={labelRequest.barcode}
														type={TitleValueType.Vertical}
													/>
												</Grid>
											</Grid>
											<Grid size={{ xs: 6, sm: 2 }}>
												<TitleValueDisplay title="Model " value={labelRequest.model} type={TitleValueType.Vertical} />
											</Grid>
											<Typography variant="body1">{labelRequest.description}</Typography>
										</Grid>

										{/* Delete Button */}
										<Grid container size={{ xs: 6, sm: 8 }} justifyContent="flex-start">
											<IconButton color="default" onClick={() => onDelete(labelRequest.sku)}>
												<DeleteOutline />
											</IconButton>
										</Grid>

										{/* Quantity Stepper */}
										<Grid size={{ xs: 6, sm: 4 }} container justifyContent="flex-end">
											<Box
												display="flex"
												alignItems="center"
												style={{ borderWidth: 1, borderColor: "grey", borderStyle: "solid" }}
											>
												<IconButton onClick={() => handleQuantityChange(labelRequest.sku, -1)} size="medium">
													<Remove />
												</IconButton>
												<ProductLabelQuantityField
													product={labelRequest}
													updateQuantity={(value) => setQuantityChange(labelRequest.sku, value)}
												/>
												<IconButton onClick={() => handleQuantityChange(labelRequest.sku, 1)} size="medium">
													<Add />
												</IconButton>
											</Box>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						))}
					</Box>
				</Grid>
			</Grid>

			{/* Label Printer */}
			<Grid style={{ marginTop: 10 }}>
				<PrinterSelect
					title="Label Printer"
					printers={availablePrinters}
					handleChange={onPrinterSelect}
					selectedPrinterId={selectedPrinter ?? ""}
				/>
			</Grid>

			{/* Template selector — shown when a printer is selected and templates are available */}
			{selectedPrinter && filteredTemplates.length > 0 && (
				<Box sx={{ mt: 2 }}>
					<FormControl fullWidth size="small">
						<InputLabel id="label-template-select-label">Label Template</InputLabel>
						<Select
							labelId="label-template-select-label"
							label="Label Template"
							value={selectedTemplate?.format ?? ""}
							onChange={onTemplateSelect}
						>
							{filteredTemplates.map((t) => (
								<MenuItem key={t.format} value={t.format}>
									{t.friendlyName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{templateRequiresSelection && (
						<Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: "block" }}>
							Please select a template to continue.
						</Typography>
					)}
				</Box>
			)}

			{/* Sheet start-position picker — shown when selected template is A4 type */}
			{selectedTemplate?.printerOutputType === "A4" && (
				<Box sx={{ my: 2 }}>
					<LabelSheetStartPicker
						columns={selectedTemplate.sheetColumns}
						rows={selectedTemplate.sheetRows}
						startColumn={startColumn}
						startRow={startRow}
						onChange={(col, row) => {
							setStartColumn(col);
							setStartRow(row);
						}}
					/>
				</Box>
			)}

			<Grid style={{ marginTop: 10 }}>
				<LargeActionButton onClick={handlePrint} disabled={isPrintButtonDisabled}>
					Print
				</LargeActionButton>
			</Grid>
		</>
	);
}
